import { useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { useOptimizedSearchSelectors } from '@/hooks/useOptimizedSearchSelectors';
import { useAppSelector } from '@/redux/hooks';
import { getSearchPageSize } from '@/redux/modules/pagination';
import {
    getMergedOptionFacet,
    getSearchFacets,
    getSelectedCategoryFacetValue,
    getSelectedFacetOptionsById,
} from '@/redux/modules/searchFacetAggregates.selectors';
import { FacetOption } from '@/types/FacetOption';
import FacetTypes from '@/types/search/enums/facetTypes';
import paramTypes from '@/types/search/enums/paramTypes';
import { getBrowseDimensionKeyFromFacetType, parseBrowseCategoryPath } from '@/utils/browsePath/browsePath';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { parseQueryParams } from '@/utils/queryParams';
import { buildSearchQuery, isFacetOptionChecked } from '@/utils/search/searchQuery';
import { parseSearchSeoPath } from '@/utils/search/searchSeoPath';
import { urlSafeTitle } from '@/utils/urls';

import { allSearchPageFilterChipsAtom, keywordFilterChipsAtom, visibleFilterChipsAtom } from '../atoms/searchPageAtoms';
import { FacetChipOption, SearchFacetItem } from '../types';

const isSubjectLikeFacetId = (facetId: string): boolean => /subject/i.test(String(facetId));

export const useGetAppliedFilterChipValues = () => {
    const formatMessage = useAppFormatMessage();
    const setAllSearchPageFilterChips = useSetAtom(allSearchPageFilterChipsAtom);
    const setKeywordFilterChips = useSetAtom(keywordFilterChipsAtom);
    const setVisibleFilterChips = useSetAtom(visibleFilterChipsAtom);

    const { pathname, search } = useLocation();
    const queryParams = parseQueryParams(search);
    const pageSize = useAppSelector(getSearchPageSize);

    // Use optimized selectors to batch Redux calls and reduce re-renders
    const { archivedSearch, searchState } = useOptimizedSearchSelectors(queryParams, pathname);
    const { isInitialLoading, submitted: isLoadingSearchResults } = searchState;

    const selectedSaleTypeOptions = useAppSelector((state) =>
        getSelectedFacetOptionsById(state, FacetTypes.SALE_TYPE_ID)
    );

    const selectedAuctionHouseOptions = useAppSelector((state) =>
        getSelectedFacetOptionsById(state, FacetTypes.AUCTION_HOUSE_ID)
    );
    const selectedMaterialsAndTechniquesOptions = useAppSelector((state) =>
        getSelectedFacetOptionsById(state, FacetTypes.MATERIALS_AND_TECHNIQUES_ID)
    );
    const materialsAndTechniquesFacet = useAppSelector((state) =>
        getMergedOptionFacet(state, FacetTypes.MATERIALS_AND_TECHNIQUES_ID)
    );

    const selectedStylePeriodOptions = useAppSelector((state) =>
        getSelectedFacetOptionsById(state, FacetTypes.STYLE_PERIOD_ID)
    );
    const stylePeriodFacet = useAppSelector((state) => getMergedOptionFacet(state, FacetTypes.STYLE_PERIOD_ID));

    const selectedCreatorOptions = useAppSelector((state) => getSelectedFacetOptionsById(state, FacetTypes.CREATOR_ID));
    const creatorFacet = useAppSelector((state) => getMergedOptionFacet(state, FacetTypes.CREATOR_ID));

    const selectedArtSubjectOptions = useAppSelector((state) =>
        getSelectedFacetOptionsById(state, FacetTypes.ART_SUBJECT_ID)
    );
    const searchFacets = useAppSelector(getSearchFacets);

    const selectedShippingOptions = useAppSelector((state) => getSelectedFacetOptionsById(state, FacetTypes.SHIPPING));

    const selectedTopRatedOptions = useAppSelector((state) =>
        getSelectedFacetOptionsById(state, FacetTypes.TOP_RATED_ID)
    );

    const selectedCountryOptions = useAppSelector((state) =>
        getSelectedFacetOptionsById(state, FacetTypes.COUNTRY_CODE_ID)
    );

    const selectedOriginCategory = useAppSelector((state) =>
        getSelectedCategoryFacetValue(state, FacetTypes.ORIGIN_ID)
    );

    const selectedL1Category = useAppSelector((state) => getSelectedCategoryFacetValue(state, FacetTypes.CATEGORY_ID));

    const convertOptionToChipValue = useCallback((options: FacetOption[], facetId: FacetTypes): FacetChipOption[] => {
        return options.map((option) => ({
            id: option.id,
            label: option.name,
            value: facetId,
        }));
    }, []);

    const getSelectedNestedFacets = useCallback((facet: SearchFacetItem | undefined): FacetOption[] => {
        const selected: FacetOption[] = [];
        if (facet.selected) {
            selected.push(facet);
        }

        // Recursively check categories
        if (facet.categories) {
            facet.categories.forEach((category) => {
                selected.push(...getSelectedNestedFacets(category));
            });
        }

        return selected;
    }, []);

    const getAllNestedFacets = useCallback((facet: SearchFacetItem | undefined): FacetOption[] => {
        if (!facet) {
            return [];
        }
        const allFacets: FacetOption[] = [facet];
        if (facet.categories) {
            facet.categories.forEach((category) => {
                allFacets.push(...getAllNestedFacets(category));
            });
        }
        return allFacets;
    }, []);

    const browsePathHasDimension = useCallback(
        (dimensionKey: string) =>
            Boolean(parseBrowseCategoryPath(pathname)?.pairs.some((pair) => pair.dimensionKey === dimensionKey)),
        [pathname]
    );

    const getCheckedOptionsForFacet = useCallback(
        (facetId: string, options: FacetOption[]) =>
            options.filter((option) =>
                isFacetOptionChecked(
                    pathname,
                    queryParams as Record<string, unknown>,
                    facetId,
                    option,
                    option.selected,
                    searchFacets
                )
            ),
        [pathname, queryParams, searchFacets]
    );

    const getSelectedOptionsWithBrowseFallback = useCallback(
        (facetId: FacetTypes, dimensionKey: string, selectedOptions: FacetOption[], facetOptions: FacetOption[]) => {
            if (browsePathHasDimension(dimensionKey)) {
                return getCheckedOptionsForFacet(facetId, facetOptions);
            }
            return selectedOptions.length > 0 ? selectedOptions : getCheckedOptionsForFacet(facetId, facetOptions);
        },
        [browsePathHasDimension, getCheckedOptionsForFacet]
    );

    const selectedMaterialsAndTechniquesOptionsWithSemanticFallback = useMemo(
        () =>
            getSelectedOptionsWithBrowseFallback(
                FacetTypes.MATERIALS_AND_TECHNIQUES_ID,
                'material',
                selectedMaterialsAndTechniquesOptions,
                materialsAndTechniquesFacet.options ?? []
            ),
        [
            getSelectedOptionsWithBrowseFallback,
            selectedMaterialsAndTechniquesOptions,
            materialsAndTechniquesFacet.options,
        ]
    );

    const selectedStylePeriodOptionsWithSemanticFallback = useMemo(
        () =>
            getSelectedOptionsWithBrowseFallback(
                FacetTypes.STYLE_PERIOD_ID,
                'style',
                selectedStylePeriodOptions,
                stylePeriodFacet.options ?? []
            ),
        [getSelectedOptionsWithBrowseFallback, selectedStylePeriodOptions, stylePeriodFacet.options]
    );

    const selectedCreatorOptionsWithSemanticFallback = useMemo(
        () =>
            getSelectedOptionsWithBrowseFallback(
                FacetTypes.CREATOR_ID,
                'creator',
                selectedCreatorOptions,
                creatorFacet.options ?? []
            ),
        [getSelectedOptionsWithBrowseFallback, selectedCreatorOptions, creatorFacet.options]
    );

    const originChips = useMemo(() => {
        const originParamName = getBrowseDimensionKeyFromFacetType(FacetTypes.ORIGIN_ID) ?? 'origin';
        const currentOriginId = queryParams[originParamName] ?? queryParams[paramTypes.CATEGORY_ID];
        if (!selectedOriginCategory || !currentOriginId) {
            return [];
        }
        const normalizedOriginValues = String(currentOriginId)
            .split(/[~,]/)
            .map((value) => value.trim())
            .filter(Boolean);
        const allOriginFacets = getAllNestedFacets(selectedOriginCategory);
        const semanticMatchedOrigins = allOriginFacets.filter((option) =>
            normalizedOriginValues.some(
                (originValue) => originValue === String(option.id) || originValue === urlSafeTitle(option.name ?? '')
            )
        );
        return convertOptionToChipValue(semanticMatchedOrigins, FacetTypes.ORIGIN_ID);
    }, [selectedOriginCategory, convertOptionToChipValue, getAllNestedFacets, queryParams]);

    const l1CategoryChips = useMemo(() => {
        // Path-based category landing pages (e.g. /c/collectibles/2/) shouldn't render
        // the category as a filter chip — only show it when it came in as a query param.
        const currentCategoryId = queryParams[paramTypes.CATEGORY_ID];
        if (!selectedL1Category || !currentCategoryId) {
            return [];
        }
        return convertOptionToChipValue(getSelectedNestedFacets(selectedL1Category), FacetTypes.CATEGORY_ID);
    }, [selectedL1Category, convertOptionToChipValue, getSelectedNestedFacets, queryParams]);

    const artSubjectChips = useMemo(() => {
        const fromQuery = Object.entries(queryParams).some(
            ([key, value]) => isSubjectLikeFacetId(key) && value !== undefined && value !== null && value !== ''
        );
        // A browse-path subject isn't removable (its "x" is a no-op); render only from a query param
        // or the SEO subject path.
        const fromSeoPath = parseSearchSeoPath(pathname)?.subjectValueSlug;
        const selectedDynamicSubjectOptions = searchFacets
            .filter((facet) => isSubjectLikeFacetId(facet.id))
            .flatMap((facet) => facet.options?.filter((option) => option.selected) ?? []);
        const selectedSubjectOptions = selectedArtSubjectOptions.length
            ? selectedArtSubjectOptions
            : selectedDynamicSubjectOptions;
        if (!selectedSubjectOptions.length || (!fromQuery && !fromSeoPath)) {
            return [];
        }
        return convertOptionToChipValue(selectedSubjectOptions, FacetTypes.ART_SUBJECT_ID);
    }, [selectedArtSubjectOptions, convertOptionToChipValue, pathname, queryParams, searchFacets]);

    // The chip's "x" only edits query params, so a path-encoded dimension (category landing) would
    // render a chip that can't be removed. Render it only when the dimension is a query param.
    const isDimensionFilterRemovableFromUrl = useCallback(
        (facetId: FacetTypes): boolean => {
            const dimensionKey = getBrowseDimensionKeyFromFacetType(facetId);
            if (!dimensionKey) {
                return false;
            }
            const paramValue = queryParams[dimensionKey];
            return paramValue !== undefined && paramValue !== null && paramValue !== '';
        },
        [queryParams]
    );

    const materialsAndTechniquesChips = useMemo(
        () =>
            isDimensionFilterRemovableFromUrl(FacetTypes.MATERIALS_AND_TECHNIQUES_ID)
                ? convertOptionToChipValue(
                      selectedMaterialsAndTechniquesOptionsWithSemanticFallback,
                      FacetTypes.MATERIALS_AND_TECHNIQUES_ID
                  )
                : [],
        [
            isDimensionFilterRemovableFromUrl,
            convertOptionToChipValue,
            selectedMaterialsAndTechniquesOptionsWithSemanticFallback,
        ]
    );

    const stylePeriodChips = useMemo(
        () =>
            isDimensionFilterRemovableFromUrl(FacetTypes.STYLE_PERIOD_ID)
                ? convertOptionToChipValue(selectedStylePeriodOptionsWithSemanticFallback, FacetTypes.STYLE_PERIOD_ID)
                : [],
        [isDimensionFilterRemovableFromUrl, convertOptionToChipValue, selectedStylePeriodOptionsWithSemanticFallback]
    );

    const creatorChips = useMemo(
        () =>
            isDimensionFilterRemovableFromUrl(FacetTypes.CREATOR_ID)
                ? convertOptionToChipValue(selectedCreatorOptionsWithSemanticFallback, FacetTypes.CREATOR_ID)
                : [],
        [isDimensionFilterRemovableFromUrl, convertOptionToChipValue, selectedCreatorOptionsWithSemanticFallback]
    );

    const getFilterTermChipsValues = useCallback((): FacetChipOption[] => {
        const filterTermQuery = queryParams[paramTypes.FILTER_TERM] || null;
        const filterTerms: string[] = filterTermQuery ? filterTermQuery.split(',') : [];

        const filterTermChips: FacetChipOption[] = filterTerms.map((term) => ({
            label: term,
            value: FacetTypes.KEYWORD_ID,
        }));

        return filterTermChips;
    }, [queryParams]);

    useEffect(() => {
        if (isLoadingSearchResults || isInitialLoading) {
            return;
        }

        const query = buildSearchQuery({
            pathname,
            queryParams,
            searchPageSize: pageSize,
            similarItems: true,
        });

        const currentBidFilterChip = archivedSearch
            ? query?.ranges?.hammerPrice && {
                  label: `Sold Price: $${query.ranges.hammerPrice.min} - $${query.ranges.hammerPrice.max}`,
                  value: FacetTypes.HAMMER_PRICE_ID,
              }
            : query?.ranges?.currentBid && {
                  label: `Current Bid: $${query.ranges.currentBid.min} - $${query.ranges.currentBid.max}`,
                  value: FacetTypes.CURRENT_BID_ID,
              };

        const priceRangeFilterChip = query?.ranges?.estimate
            ? {
                  label: `Price Estimate: $${query.ranges.estimate.min} - $${query.ranges.estimate.max}`,
                  value: FacetTypes.ESTIMATED_PRICE_ID,
              }
            : null;

        const buyersPremiumFilterChip = query?.ranges?.buyersPremium
            ? {
                  label: String(
                      formatMessage(['searchFacetBuyersPremiumChipLabel'], {
                          values: { max: query.ranges.buyersPremium.max, min: query.ranges.buyersPremium.min },
                      })
                  ),
                  value: FacetTypes.BUYERS_PREMIUM_RANGE_ID,
              }
            : null;

        const filterTermChipsFormatted = getFilterTermChipsValues();

        const auctionHouseChips = convertOptionToChipValue(selectedAuctionHouseOptions, FacetTypes.AUCTION_HOUSE_ID);

        const visibleFilterChipsFormatted = [
            currentBidFilterChip,
            priceRangeFilterChip,
            buyersPremiumFilterChip,
            ...filterTermChipsFormatted,
            ...convertOptionToChipValue(selectedSaleTypeOptions, FacetTypes.SALE_TYPE_ID),
            ...materialsAndTechniquesChips,
            ...stylePeriodChips,
            ...creatorChips,
            ...artSubjectChips,
            ...originChips,
        ].filter((chip) => Boolean(chip));

        const allFilterChipsFormatted = [
            ...l1CategoryChips,
            ...visibleFilterChipsFormatted,
            ...auctionHouseChips,
            ...convertOptionToChipValue(selectedShippingOptions, FacetTypes.SHIPPING),
            ...convertOptionToChipValue(selectedTopRatedOptions, FacetTypes.TOP_RATED_ID),
            ...convertOptionToChipValue(selectedCountryOptions, FacetTypes.COUNTRY_CODE_ID),
        ].filter((chip) => Boolean(chip));

        setAllSearchPageFilterChips(allFilterChipsFormatted);
        setKeywordFilterChips(filterTermChipsFormatted);
        setVisibleFilterChips(visibleFilterChipsFormatted);
    }, [
        formatMessage,
        isLoadingSearchResults,
        isInitialLoading,
        archivedSearch,
        pathname,
        queryParams,
        pageSize,
        getFilterTermChipsValues,
        convertOptionToChipValue,
        selectedSaleTypeOptions,
        selectedAuctionHouseOptions,
        materialsAndTechniquesChips,
        stylePeriodChips,
        creatorChips,
        artSubjectChips,
        originChips,
        selectedShippingOptions,
        selectedTopRatedOptions,
        selectedCountryOptions,
        setAllSearchPageFilterChips,
        setKeywordFilterChips,
        setVisibleFilterChips,
        l1CategoryChips,
    ]);
};
